/**
 * Layout dos documentos comerciais INTTRIX aprovado pela Valéria em 08/10/2026 (mockup): Proposta Comercial de Venda
 * e Ordem de Compra ao Fornecedor. Só APRESENTAÇÃO: os dados chegam prontos de quem chama (nenhuma regra aqui).
 *  - cabeçalho com o logo oficial, o slogan oficial, o título e a identificação do documento;
 *  - blocos numerados (faixa azul-clara, número e título em azul), cards, divisórias e ícones lineares;
 *  - impressão/PDF com as mesmas faixas e cards, sem depender da opção "gráficos de fundo" (CSS .doc2);
 *  - paginação natural: blocos e linhas de tabela não se partem; tabelas longas continuam com o cabeçalho repetido.
 * Marca-d'água (prévia sem os dados da INTTRIX, rascunho, ambiente de teste) e o rodapé com os dados da INTTRIX
 * seguem as mesmas regras de antes.
 */
import type { ReactNode } from 'react';
import { useAmbiente } from '../../ambiente.tsx';
import type { DadosEmpresa, Endereco } from '../../../../backend/src/compartilhado/propostas.ts';

const ICONES = {
  predio: '<rect x="4" y="3" width="10" height="18" rx="1"/><path d="M14 9h5a1 1 0 0 1 1 1v11H14M7 7h1M10 7h1M7 11h1M10 11h1M7 15h1M10 15h1M17 13h1M17 17h1M8 21v-3h2v3"/>',
  documento: '<rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 9h8M8 13h5M8 2v4M16 2v4"/><path d="M14 16l1.5 1.5L18 15"/>',
  pessoa: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5"/>',
  email: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>',
  telefone: '<path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"/>',
  calendario: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4M7 14h3M7 17h6"/>',
  caminhao: '<path d="M2 6h11v10H2zM13 9h4l4 4v3h-8z"/><circle cx="6.5" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/>',
  relogio: '<circle cx="12" cy="13" r="8"/><path d="M12 9v4l3 2M9 2h6"/>',
  nota: '<rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 8h6M9 12h6M9 16h3"/>',
} as const;
export type NomeIcone = keyof typeof ICONES;

/** Ícone linear (desenho fixo do layout aprovado; nunca recebe conteúdo externo). */
export function Icone({ nome, grande }: { nome: NomeIcone; grande?: boolean }) {
  return <svg className={grande ? 'd-ico-g' : 'd-ico'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden dangerouslySetInnerHTML={{ __html: ICONES[nome] }} />;
}

/** Endereço em linhas: logradouro e número (complemento), bairro, cidade/UF e CEP. */
export function linhasEndereco(e: Endereco): string[] {
  const l1 = [e.logradouro, e.numero].filter(Boolean).join(', ') + (e.complemento ? ` — ${e.complemento}` : '');
  const cep = /^\d{8}$/.test(e.cep ?? '') ? `${e.cep.slice(0, 5)}-${e.cep.slice(5)}` : e.cep; // só exibição
  const l3 = [e.cidade && e.uf ? `${e.cidade}/${e.uf}` : e.cidade || e.uf, cep ? `CEP ${cep}` : ''].filter(Boolean).join(' — ');
  return [l1, e.bairro, l3].filter((x) => x && x.trim());
}
export const mesmoEndereco = (a: Endereco, b: Endereco) => linhasEndereco(a).join('|') === linhasEndereco(b).join('|');

export function Linhas({ linhas }: { linhas: string[] }) {
  return (
    <>
      {linhas.map((x, i) => (
        <div className="d-v" key={i}>
          {x}
        </div>
      ))}
    </>
  );
}

/** Pares rótulo × valor (grade de duas colunas). */
export function Pares({ pares }: { pares: [string, ReactNode, boolean?][] }) {
  return (
    <div className="d-pares">
      {pares.map(([r, v, forte]) => (
        <span key={r} style={{ display: 'contents' }}>
          <span className="d-r">{r}</span>
          <span className={`d-v${forte ? ' d-forte' : ''}`}>{v === '' || v === null || v === undefined ? '—' : v}</span>
        </span>
      ))}
    </div>
  );
}

export function Bloco({ n, titulo, extra, teste, quebra, children }: { n: number; titulo: string; extra?: string; teste?: string; quebra?: boolean; children: ReactNode }) {
  return (
    <section className={`d-bloco${quebra ? ' d-quebra' : ''}`} data-teste={teste}>
      <div className="d-faixa">
        <span className="d-n">{n}</span>
        <h2>
          {titulo}
          {extra && <small>{extra}</small>}
        </h2>
      </div>
      {children}
    </section>
  );
}

const textoEmpresa = (e: DadosEmpresa) => [`${e.razao} | CNPJ ${e.cnpj}`, e.endereco, [e.telefone, e.email, e.site].filter(Boolean).join(' · ')].filter(Boolean).join(' — ');

/**
 * Folha do documento: barra de ações (fora da impressão), cabeçalho do layout aprovado, marca-d'água e rodapé.
 * `ident`: [rótulo, valor, ocupa a linha inteira?].
 */
export function DocumentoInttrix({ titulo, subtitulo, ident, colunasIdent, empresa, rascunho, avisoRascunho, voltar, teste, children, paginado }: { titulo: string; subtitulo?: string; ident: [string, ReactNode, boolean?][]; colunasIdent: number; empresa: DadosEmpresa | null; rascunho?: string; avisoRascunho?: string; voltar: string; teste: string; children: ReactNode; /** P4 bloco 3 (D6): "Página X de Y" no rodapé de cada página impressa (OCF e OL) */ paginado?: boolean }) {
  const { faixa } = useAmbiente();
  const marca = !empresa ? 'PRÉVIA — DADOS DA INTTRIX NÃO CONFIGURADOS' : rascunho ? rascunho : faixa ? 'AMBIENTE DE TESTE — DADOS FICTÍCIOS' : '';
  const aviso = !empresa ? 'Os dados da INTTRIX nos documentos (razão social, CNPJ, endereço e contatos) aguardam cadastro em Parâmetros (P-18): este documento é só uma prévia.' : rascunho ? (avisoRascunho ?? '') : '';
  return (
    <>
      <div className="barra-doc">
        <a className="botao-secundario" href={`#${voltar}`}>
          ← Voltar
        </a>
        <button type="button" className="botao-primario" onClick={() => window.print()} data-teste="imprimir-pdf">
          Imprimir / salvar como PDF
        </button>
        {aviso && <span className="texto-secundario">{aviso}</span>}
      </div>
      <article className={`folha doc2${paginado ? ' doc-paginado' : ''}`} data-teste={teste} aria-label={titulo}>
        {marca && (
          <div className="marca-dagua" aria-hidden data-teste="marca-dagua">
            {marca}
          </div>
        )}
        <header className="d-cab">
          <div className="d-marca">
            <img src="marca/inttrix-logo-horizontal.png" alt="INTTRIX Technology" width="134" height="42" data-teste="logo-documento" />
            <div className="d-slogan">Tecnologia empresarial multimarcas.</div>
            <div className="d-slogan-comp">Do usuário ao data center, um ecossistema completo de tecnologia.</div>
          </div>
          <div className="d-titulo">
            <h1>{titulo}</h1>
            {subtitulo && <div className="d-sub">{subtitulo}</div>}
            <div className="d-ident" style={{ gridTemplateColumns: `repeat(${colunasIdent}, auto)` }} data-teste="identificacao-documento">
              {ident.map(([r, v, largo]) => (
                <div key={r} style={largo ? { gridColumn: '1 / -1' } : undefined}>
                  <div className="d-r">{r}</div>
                  <div className="d-v">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </header>
        {children}
        <footer className="d-rodape">
          <span data-teste="dados-inttrix">{empresa ? textoEmpresa(empresa) : 'Dados da INTTRIX a configurar (P-18)'}</span>
          {aviso && <div>{aviso}</div>}
        </footer>
      </article>
    </>
  );
}
